import { useCallback } from 'react';
import type { FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayInsert, UseFieldArrayMove, UseFieldArrayRemove, UseFieldArrayUpdate } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import type { SequenceFormFields } from '@/validations/sequence';

type StepControls = {
  append: UseFieldArrayAppend<SequenceFormFields, 'steps'>;
  insert: UseFieldArrayInsert<SequenceFormFields, 'steps'>;
  remove: UseFieldArrayRemove;
  move: UseFieldArrayMove;
  update: UseFieldArrayUpdate<SequenceFormFields, 'steps'>;
  fields: FieldArrayWithId<SequenceFormFields, 'steps', 'id'>[];
};

/**
 * Hook to handle step management operations (add, remove, move, etc.)
 * Now works alongside useGrammarTextareaHandlers for keyboard event handling
 */
export default function useStepKeyboardNavigation(stepControls: StepControls) {
  const { append, insert, remove, move, update, fields } = stepControls;

  /**
   * Focus a specific step's textarea
   */
  const focusStep = useCallback((index: number) => {
    setTimeout(() => {
      const textarea = document.querySelector(`textarea[name="steps.${index}.text"]`) as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
        // Position cursor at the end of text
        const textLength = textarea.value.length;
        textarea.setSelectionRange(textLength, textLength);
      }
    }, 0);
  }, []);

  /**
   * Create a new empty step at the end of the sequence
   */
  const addStep = useCallback(() => {
    const stepId = uuidv4();
    append({
      text: '',
      id: stepId,
      imageInfo: null,
      hasCheckpointPrompt: false,
    });

    // Focus the newly added step
    setTimeout(() => {
      const lastIndex = fields.length;
      focusStep(lastIndex);
    }, 0);
  }, [append, fields.length, focusStep]);

  /**
   * Add a step at a specific index with optional text
   */
  const addStepAtIndex = useCallback((index: number, text?: string) => {
    const stepId = uuidv4();
    insert(index, {
      text: text || '',
      id: stepId,
      imageInfo: null,
      hasCheckpointPrompt: false,
    });
  }, [insert]);

  /**
   * Remove a step at the specified index
   */
  const removeStep = useCallback((index: number) => {
    remove(index);
  }, [remove]);

  /**
   * Move a step from one index to another
   */
  const moveStep = useCallback((fromIndex: number, toIndex: number) => {
    if (toIndex >= 0 && toIndex < fields.length) {
      move(fromIndex, toIndex);
    }
  }, [fields.length, move]);

  /**
   * Merge the current step's text with the previous step and remove the current step
   */
  const mergeWithPreviousStep = useCallback((currentStepIndex: number, currentStepText: string) => {
    if (currentStepIndex <= 0 || currentStepIndex >= fields.length) {
      return; // Can't merge first step or invalid index
    }

    const previousStep = fields[currentStepIndex - 1];
    if (!previousStep) {
      return;
    }

    // Get the previous step's current text
    const previousStepText = previousStep.text || '';

    // Merge texts with a space in between (only if both have content)
    const mergedText = previousStepText && currentStepText
      ? `${previousStepText} ${currentStepText}`
      : previousStepText + currentStepText;

    // Update the previous step with merged text
    update(currentStepIndex - 1, {
      ...previousStep,
      text: mergedText,
    });

    // Remove the current step
    remove(currentStepIndex);

    // Focus the previous step and position cursor at the end of the original text
    setTimeout(() => {
      const textarea = document.querySelector(`textarea[name="steps.${currentStepIndex - 1}.text"]`) as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
        // Position cursor at the end of the previous text (before the merged text)
        const cursorPosition = previousStepText.length + (previousStepText ? 1 : 0); // +1 for the space if there was previous text
        textarea.setSelectionRange(cursorPosition, cursorPosition);
      }
    }, 0);
  }, [fields, update, remove]);

  return {
    // Step management functions
    addStep,
    addStepAtIndex,
    removeStep,
    moveStep,
    focusStep,
    mergeWithPreviousStep,
  };
}
